import React, {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react';
import { withViewTransition } from '@datanomik/core-ui';

import { NAV_CHROME_LS_KEY, resolveNavChrome, type NavChrome } from '@/config/navChrome';

interface NavChromeValue {
  chrome: NavChrome;
  setChrome: (next: NavChrome) => void;
}

const NavChromeContext = createContext<NavChromeValue | null>(null);

const persistNavChrome = (next: NavChrome): void => {
  try {
    window.localStorage.setItem(NAV_CHROME_LS_KEY, next === 'top' ? 'true' : 'false');
  } catch {
    return;
  }
};

export const NavChromeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [chrome, setChromeState] = useState<NavChrome>(resolveNavChrome);

  const setChrome = useCallback((next: NavChrome) => {
    persistNavChrome(next);
    withViewTransition(() => setChromeState(next), 'shell');
  }, []);

  const value = useMemo(() => ({ chrome, setChrome }), [chrome, setChrome]);

  return <NavChromeContext.Provider value={value}>{children}</NavChromeContext.Provider>;
};

export const useNavChrome = (): NavChromeValue => {
  const ctx = useContext(NavChromeContext);
  if (!ctx) throw new Error('useNavChrome must be used within a NavChromeProvider');
  return ctx;
};
